<template>
    <div
        :id="id"
        ref="containerRef"
        class="tabbed-container-block"
        :data-index-path="indexPath"
    >
        <div
            v-if="!customPageBuilder"
            class="tabs"
        >
            <template
                v-for="(label, index) in visibleLabels"
                :key="index"
            >
                <div
                    v-if="label"
                    class="tab"
                    :class="{selected: activeTabIndex === index}"
                    :data-testid="`tab-${index}`"
                    @click="handleTabClick(index)"
                >
                    <span
                        class="label"
                        :title="label"
                    >
                        {{ label }}
                    </span>
                </div>
            </template>
        </div>

        <template
            v-for="(child, index) in children"
            :key="child.id"
        >
            <CustomPageBlock
                :block="child"
                :model-value="child.currentValue"
                :data-builder="customPageBuilder"
                :data-label="labels[index]"
                :data-is-visible="isVisible(index)"
                :data-active-tab="activeTabIndex === index"
                :index-path="[
                    ...Array.isArray(indexPath)
                        ? indexPath
                        : [indexPath],
                    index
                ]"
                :data-testid="`custom-page-block-${kebabCase((child as ExtendedBlock<CPInputConfiguration>).fieldName || child.type)}`"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { kebabCase } from 'change-case'
import { inject, ref, computed, watch, onMounted } from 'vue'
import type { GetConditionValueInject } from 'widget-components/composables/conditions/useConditions'
import { CustomPageBlock } from 'widget-components/integrations/CustomPage'
import type { CPInputConfiguration, ExtendedBlock } from 'widget-components/integrations/CustomPage/types'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'

const props = defineProps<{
    id?: string
    children: ExtendedBlock[]
    labels: string[]
    indexPath?: number[]
}>()

defineOptions({ inheritAttrs: false })

const customPageBuilder = inject<boolean>('customPageBuilder', false)
const getDisplayValue = inject<GetConditionValueInject>('getDisplayValue', (_: string) => true)
const reportAutofocusDisplayed = inject<(id: string, displayed: boolean) => void>('reportAutofocusDisplayed')

/*
 * V-show imitation
 * Tabs can't unmount because the components will lose their values
 * and be replaced by initialValue.
 * Classic v-show doesn't work because css display is set by styles later.
*/
const isVisible = (index: number) => customPageBuilder || activeTabIndex.value === index

const visibleLabels = computed(() => props.labels
    .map((label, idx) => customPageBuilder
        ? label
        : (props.children[idx] && getDisplayValue(props.children[idx].id)) ? label : ''))

const activeTabIndex = ref(visibleLabels.value.findIndex(l => !!l))

const handleTabClick = (index: number) => {
    activeTabIndex.value = index
}

watch(visibleLabels, (newLabels) => {
    if (activeTabIndex.value === -1) {
        activeTabIndex.value = newLabels.findIndex(l => !!l)
    }
})

const reportTabVisibility = (idx: number, displayed: boolean) => {
    if (!reportAutofocusDisplayed || !props.children[idx]) return
    flattenBlocks([props.children[idx]]).forEach(b => reportAutofocusDisplayed(b.id, displayed))
}

onMounted(() => {
    props.children.forEach((_, idx) => reportTabVisibility(idx, idx === activeTabIndex.value))
})

watch(activeTabIndex, (newIdx, oldIdx) => {
    if (oldIdx !== undefined) reportTabVisibility(oldIdx, false)
    reportTabVisibility(newIdx, true)
})

</script>

<style lang="sass" scoped>
@import "./TabbedContainer.sass"
</style>
